import { type ReactElement, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AppImage } from '~/components/ui/AppImage';
import { cn } from '~/utils';

const SOAP_ICON = '/soap.png';

export type CreditLabelVariant = 'badge' | 'inline' | 'display' | 'value';

export type FormatCreditBalanceOptions = {
  maximumFractionDigits?: number;
};

export const formatCreditBalance = (
  value: string | number | null,
  locale: string,
  options: FormatCreditBalanceOptions = {}
): string => {
  if (value === null) return '—';
  const balance = Number(value);
  return Number.isFinite(balance)
    ? new Intl.NumberFormat(locale, {
        maximumFractionDigits: options.maximumFractionDigits ?? 0,
      }).format(balance)
    : '—';
};

export const formatCreditAmountText = (
  balance: string | number | null,
  locale: string,
  unitLabel: string,
  options?: FormatCreditBalanceOptions
): string => `${formatCreditBalance(balance, locale, options)} ${unitLabel}`;

const CreditIcon = ({ className }: { className?: string }): ReactElement => (
  <img
    alt=""
    aria-hidden="true"
    className={cn('shrink-0 object-contain', className)}
    height={20}
    src={SOAP_ICON}
    width={20}
  />
);

const variantStyles: Record<CreditLabelVariant, string> = {
  badge:
    'inline-flex min-h-9 shrink-0 items-center gap-1 rounded-lg border border-border bg-elevated px-2 text-[0.6875rem] font-semibold tabular-nums text-muted',
  inline:
    'inline-flex shrink-0 items-center gap-1 font-semibold tabular-nums text-inherit',
  display:
    'inline-flex items-center gap-2 font-mono text-4xl font-semibold tabular-nums text-foreground',
  value:
    'inline-flex items-center gap-1 font-mono text-sm font-semibold tabular-nums text-foreground',
};

const iconSizes: Record<CreditLabelVariant, string> = {
  badge: 'size-4',
  inline: 'size-3.5',
  display: 'size-8',
  value: 'size-3.5',
};

export const CreditLabel = ({
  balance,
  locale,
  className,
  variant = 'badge',
  showUnit = true,
  hideUnitOnMobile = false,
  showIcon = true,
  sign,
  maximumFractionDigits,
}: {
  balance: string | number | null;
  locale: string;
  className?: string;
  variant?: CreditLabelVariant;
  showUnit?: boolean;
  hideUnitOnMobile?: boolean;
  showIcon?: boolean;
  sign?: '+' | '−' | '';
  maximumFractionDigits?: number;
}): ReactElement => {
  const { t: tc } = useTranslation('common');
  const formattedBalance = useMemo(
    () => formatCreditBalance(balance, locale, { maximumFractionDigits }),
    [balance, locale, maximumFractionDigits]
  );

  return (
    <span
      aria-live={variant === 'badge' ? 'polite' : undefined}
      className={cn(variantStyles[variant], className)}
    >
      {sign ? <span className="whitespace-nowrap">{sign}</span> : null}
      {showIcon ? <CreditIcon className={iconSizes[variant]} /> : null}
      <span className="whitespace-nowrap">{formattedBalance}</span>
      {showUnit ? (
        <span
          className={cn(
            'whitespace-nowrap',
            hideUnitOnMobile && 'hidden sm:inline'
          )}
        >
          {tc('credits')}
        </span>
      ) : null}
    </span>
  );
};

export const CreditChangeLabel = ({
  amount,
  locale,
  className,
}: {
  amount: string;
  locale: string;
  className?: string;
}): ReactElement => {
  const numeric = Number(amount);
  const sign = numeric < 0 ? '−' : numeric > 0 ? '+' : '';
  const absoluteAmount = amount.startsWith('-') ? amount.slice(1) : amount;

  return (
    <CreditLabel
      balance={absoluteAmount}
      className={className}
      locale={locale}
      maximumFractionDigits={6}
      showUnit={false}
      sign={sign}
      variant="value"
    />
  );
};
